import React, { useEffect, useState, useMemo, useRef } from "react";
import Select from "react-select";
import ModalManager from "@ebay/nice-modal-react";
import ApiModal from "/src/components/modal/ApiModal/ApiModal";

import { AgGridReact } from "ag-grid-react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { PageTable, Header } from "/src/layouts/page";
import ConversationCompletedService from "../../../services/ConversationCompletedService";
import Icons from '/src/features/supervision/conversations/data/Icons';
import { formatDateWithoutSeconds } from '/src/utils/dateWithoutSeconds';
import formatPhoneNumber from '/src/utils/formatPhoneNumber';
import { getDuration } from '../../../data/getDuration';

import ProgressBarCircular from "../../ProgressBarCircular/ProgressBarCircular";
import StarRating from '../../StarRating/StarRating';
import TopFailCriteria from '../../TopFailCriteria/TopFailCriteria';
import TopicTag from '../../TopicTag/TopicTag';
import ConversationMarker from '../../ConversationMarker/ConversationMarker';
import ReviewersBubbles from '../../ReviewersBubbles/ReviewersBubbles';
import LiveUpdateIndicator from './LiveUpdateIndicator';

import './ConversationTableCompleted.css';
import '/src/components/UI/Tabs/Tabs.css';
import getCutToken from "/src/utils/getCutToken";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import InputSearch from "/src/components/UI/InputSearch/InputSearch";

import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import storage from "/src/utils/storage";
import useUnitLabels from "/src/hooks/useUnitLabels";
import hasPermission from "/src/utils/hasPermission";
import cleanConversationFilters from "/src/features/supervision/conversations/utils/cleanConversationFilters";
import AudioAnalysisModal from "../../AudioAnalysisModal/AudioAnalysisModal";
import BulkRefreshModal from "../../BulkRefreshModal/BulkRefreshModal";
import useBulkRefreshJobs from "../../../hooks/useBulkRefreshJobs";
import { buildCriteriaKey, keepCriteriaKeys } from "/src/utils/criteriaKey";

const CRITERIA_CATEGORY_COLORS = [
    "#4c9aff", "#36b37e", "#ff8b00", "#c33b8f",
    "#00b8d9", "#6554c0", "#ff5630", "#8993a4"
];

function ConversationTableCompleted() {

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { getConversations, getCriteriasAll, processRefresh, getConversationsPresence, deleteConversation } = ConversationCompletedService;

    const user = storage('local').get("user");
    const bulkRefresh = useBulkRefreshJobs(Boolean(user?.is_admin));
    const { getLabel: getUnitLabel, labelByUnitId, unitLabels } = useUnitLabels();

    // "view_own_conversations" without "view_conversations" means the user can only
    // ever see their own conversations - force/lock the agent filter to their email.
    const isRestrictedToOwnConversations = !hasPermission('view_conversations') && hasPermission('view_own_conversations');

    const toast = useToastDispatchContext();
    const [criteriasByCategory, setCriteriasByCategory] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const FILTERS_STORAGE_KEY = "conversations_filters";
    const getDefaultFilters = () => ({
        call_id: "",
        client_id: "",
        agent_id: isRestrictedToOwnConversations ? (user?.email || "") : "",
        unit_ids: [],
        criteria_ids: [],
        criteria_categories: [],
        criteria_results: [],
        date_start: "",
        date_end: "",
        score_min: 0,
        score_max: 100,
        processed: true
    });

    const [filters, setFilters] = useState(() => {
        const saved = storage().get(FILTERS_STORAGE_KEY);
        const defaults = getDefaultFilters();
        const restored = saved && typeof saved === "object" ? { ...defaults, ...saved } : defaults;
        restored.criteria_ids = keepCriteriaKeys(restored.criteria_ids);
        restored.criteria_categories = [];
        if (isRestrictedToOwnConversations) {
            restored.agent_id = user?.email || "";
        }
        return restored;
    });

    const [filterResetKey, setFilterResetKey] = useState(0);

    useEffect(() => {
        // Lets other pages (e.g. the analytics/agents-performance dashboards)
        // deep-link here with a criteria/agent/result preset already applied -
        // via router state (same-tab SPA nav) or query params (new tab). Runs
        // on every navigation to this route, not just first mount, since
        // React Router reuses this component across same-path navigations.
        const presetFromQuery = {};
        const criteriaIdsFromQuery = keepCriteriaKeys(searchParams.getAll("criteria_ids"));
        if (criteriaIdsFromQuery.length) presetFromQuery.criteria_ids = criteriaIdsFromQuery;
        const criteriaCategoriesFromQuery = searchParams.getAll("criteria_categories");
        if (criteriaCategoriesFromQuery.length) presetFromQuery.criteria_categories = criteriaCategoriesFromQuery;
        const criteriaResultsFromQuery = searchParams.getAll("criteria_results");
        if (criteriaResultsFromQuery.length) presetFromQuery.criteria_results = criteriaResultsFromQuery;
        const agentIdFromQuery = searchParams.get("agent_id");
        if (agentIdFromQuery) presetFromQuery.agent_id = agentIdFromQuery;

        const hasStatePreset = location.state?.presetFilters !== undefined;
        const preset = { ...presetFromQuery, ...(location.state?.presetFilters || {}) };
        if (Object.keys(presetFromQuery).length || hasStatePreset) {
            setFilters({ ...getDefaultFilters(), ...preset });
            setFilterResetKey((k) => k + 1);
        }
    }, [location.search, location.state]);

    useEffect(() => {
        const { criteria_categories, ...persistable } = filters;
        storage().set(FILTERS_STORAGE_KEY, persistable);
    }, [filters]);

    const removeScorecardFromUrl = () => {
        const url = new URL(window.location.href);
        if (!url.searchParams.has("criteria_categories")) return;
        url.searchParams.delete("criteria_categories");
        window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    };

    const handleRemoveScorecard = (category) => {
        setFilters(prev => ({
            ...prev,
            criteria_categories: (prev.criteria_categories || []).filter((value) => value !== category)
        }));
        removeScorecardFromUrl();
    };

    const handleResetFilters = () => {
        removeScorecardFromUrl();
        storage().remove(FILTERS_STORAGE_KEY);
        setFilters(getDefaultFilters());
        setFilterResetKey((k) => k + 1);
    };

    const hasActiveFilters = useMemo(() => {
        const defaults = getDefaultFilters();
        return Object.keys(defaults).some((key) => {
            const current = filters[key];
            const base = defaults[key];
            if (Array.isArray(current) || Array.isArray(base)) {
                return JSON.stringify(current || []) !== JSON.stringify(base || []);
            }
            return current !== base;
        });
    }, [filters]);

    const [gridApi, setGridApi] = useState(null);
    const presenceByCallId = useRef({});
    const [lastPresenceUpdate, setLastPresenceUpdate] = useState(null);

    const cleanedFilters = useMemo(
        () => cleanConversationFilters(filters, getDefaultFilters()),
        [filters]
    );

    const canViewReviewing = user?.is_admin || hasPermission('view_reviewing');

    useEffect(() => {
        if (!gridApi || !canViewReviewing) return;

        const fetchPresence = async () => {
            const callIds = gridApi.getRenderedNodes().map((node) => node.data?.call_id).filter(Boolean);
            if (callIds.length === 0) return;
            const [response, error] = await getConversationsPresence(callIds);
            if (error || !response) return;
            presenceByCallId.current = response.data || {};
            gridApi.refreshCells({ columns: ["reviewing"], force: true });
            setLastPresenceUpdate(Date.now());
        };

        fetchPresence();
        const interval = setInterval(fetchPresence, 15000);
        return () => clearInterval(interval);
    }, [gridApi, getConversationsPresence, canViewReviewing]);

    // Total the grid is actually displaying right now, kept in sync by
    // datasource.getRows below (including right after a purge, since that
    // triggers getRows again) - the 15s check compares against this, not
    // against its own first reading, otherwise a change arriving before the
    // very first tick gets silently adopted as the new "normal" and never
    // triggers a refresh.
    const displayedTotalRef = useRef(null);

    // Every 15s, check whether the total count of conversations matching the
    // current filters changed (new ones ingested, some deleted/filtered out
    // elsewhere) and purge the grid's cache to pull the fresh list - without
    // this, the table only ever refreshes on a manual filter change or a
    // full page reload.
    useEffect(() => {
        if (!gridApi) return;

        const checkForNewConversations = async () => {
            try {
                const [response] = await getConversations({ page: 1, limit: 1, ...cleanedFilters });
                const total = response?.total ?? 0;
                if (displayedTotalRef.current !== null && total !== displayedTotalRef.current) {
                    gridApi.purgeInfiniteCache();
                }
            } catch (e) {
                console.error(e);
            }
        };

        const interval = setInterval(checkForNewConversations, 15000);
        return () => clearInterval(interval);
    }, [gridApi, getConversations, cleanedFilters]);

    const datasource = useMemo(() => ({
        getRows: async (params) => {
            setIsLoading(true)
            const startRow = params.startRow;
            const endRow = params.endRow;

            const limit = endRow - startRow;
            const page = Math.floor(startRow / limit) + 1;
            try {
                const [response] = await getConversations({
                    page,
                    limit,
                    ...cleanedFilters
                });

                displayedTotalRef.current = response.total || 0;
                params.successCallback(
                    response.data || [],
                    response.total || 0
                );
            } catch (e) {
                console.error(e);
                params.failCallback();
            }

            setIsLoading(false)
        }
    }), [getConversations, cleanedFilters]);

    const onClickView = ({ row }) =>
        navigate("/supervision/conversation/profil/" + row.call_id, {
            state: { data: row },
        });

    const onClickRefresh = async ({ row }) => {
        const { id, call_id } = row;
        await ModalManager.show(ApiModal, {
            title: "Refresh conversation " + row.call_id,
            body: "Do you really want to refresh the conversation " + call_id + " ?",
            api: {
                request: processRefresh,
                param: [{ "call_id": row.call_id }],
            },
            buttons: [
                { key: "cancel", className: "cancel", label: "Cancel" },
                { key: "validate", className: "validate", label: "Refresh", type: "submit" },
            ],
            confirm: {
                name: row.call_id,
                fieldName: "the conversation",
            },
        });
    }

    const onClickDelete = async ({ row }) => {
        const result = await ModalManager.show(ApiModal, {
            title: "Delete conversation " + row.call_id,
            body: "Do you really want to delete the conversation " + row.call_id + " ? This cannot be undone.",
            api: {
                request: deleteConversation,
                param: [row.call_id],
            },
            buttons: [
                { key: "cancel", className: "cancel", label: "Cancel" },
                { key: "validate", className: "validate", label: "Delete", type: "submit" },
            ],
            confirm: {
                name: row.call_id,
                fieldName: "the conversation",
            },
        });

        if (result?.error != null) {
            toast.error("Error", "Could not delete the conversation.");
            return;
        }

        toast.success("Conversation deleted", "The conversation has been removed.");
        gridApi?.purgeInfiniteCache();
    }

    useEffect(() => {
        if (gridApi) {
            gridApi.purgeInfiniteCache();
        }
    }, [filters, gridApi]);
    // Colonnes AG Grid
    const columnDefs = useMemo(() => [
        {
            headerName: "Channel",
            field: "meta_data.call_direction",
            width: 100,
            minWidth: 80,
            cellRenderer: (params) => (
                <TopicTag value={params.value} />
            )
        },
        {
            headerName: "Call ID",
            field: "call_id",
            width: 100,
            cellRenderer: (params) => (
                <span
                    style={{ color: "var(--main)", cursor: "pointer" }}
                    onClick={(e) =>
                        navigator.clipboard.writeText(params.value).then((_) => {
                            toast.success("Copy ID to clipboard", "You can paste this ID where you need !", 5000);
                        })
                    }>
                    {getCutToken(params.value)}
                </span>
            )
        },
        {
            headerName: "Date",
            field: "timestamp",
            sortable: true,
            width: 150,
            valueGetter: (params) => params.data?.timestamp || params.data?.created_at,
            valueFormatter: (params) => !params.value ? "No date" : formatDateWithoutSeconds(params.value, false)
        },
        {
            headerName: "Agent",
            field: "agent_id",
            flex: 1,
            minWidth: 130,
            cellRenderer: (params) => {
                return (
                    <span
                        style={{ color: "var(--main)", cursor: "pointer" }}
                        onClick={(e) =>
                            navigator.clipboard.writeText(params.value).then((_) => {
                                toast.success("Copy ID to clipboard", "You can paste this ID where you need !", 5000);
                            })
                        }>
                        {params?.value?.split("@")[0]}
                    </span>
                )
            }
        },
        {
            headerName: "Unit",
            field: "unit_id",
            flex: 1,
            minWidth: 130,
            valueFormatter: (params) => getUnitLabel(params.value)
        },
        {
            headerName: "Client ID",
            field: "meta_data.contact_id",
            flex: 1,
            minWidth: 130
        },
        {
            headerName: "Total Time",
            field: "talk_stats.total_duration",
            width: 110,
            valueFormatter: (params) => params.value == null ? "No value" : getDuration(params.value)
        },
        /*
        {
            headerName: "Caller",
            field: "caller",
            width: 150,
            valueFormatter: (params) => formatPhoneNumber(params.value)
        },
        {
            headerName: "Line",
            field: "called",
            width: 100,
            valueFormatter: (params) => formatPhoneNumber(params.value)
        },*/

        {
            headerName: "Talk Rate",
            field: "talk_stats.speakers.1.percentage_speaking",
            width: 100,
            cellRenderer: (params) => (
                <ProgressBarCircular value={params.value} size={50} />
            )
        },
        {
            headerName: "Compliance score",
            field: "score",
            width: 150,
            cellRenderer: (params) => (
                <StarRating score={params.value} />
            )
        },
        {
            headerName: "Failed Criteria",
            field: "criteria_conversations",
            minWidth: 130,
            width: 150,
            cellRenderer: (params) => (
                <TopFailCriteria criterias={params.value} />
            )
        },
        /*
        {
            headerName: "Markers",
            field: "markers",
            width: 100,
            cellRenderer: (params) => (
                <div className='list_makers_conversation_table'>
                    {params.value?.map((marker, i) =>
                        i < 4 && (
                            <ConversationMarker
                                key={i}
                                marker={marker}
                                className={i !== 0 ? "superposition_maker_conversation_circle" : ""}
                                style={{ left: "-" + 9 * i + "px", zIndex: i }}
                            />
                        )
                    )}
                </div>
            )

        },*/
        (user?.is_admin || hasPermission('view_reviewed')) && {
            headerName: "Reviewed",
            field: "reviewers",
            width: 120,
            minWidth: 100,
            cellClass: "reviewed-cell",
            cellRenderer: (params) => (
                <ReviewersBubbles reviewers={params.value} />
            )
        },
        (user?.is_admin || hasPermission('view_reviewing')) && {
            colId: "reviewing",
            headerName: "Reviewing",
            width: 120,
            minWidth: 100,
            sortable: false,
            filter: false,
            cellClass: "reviewed-cell",
            cellRenderer: (params) => (
                <ReviewersBubbles className="reviewers-bubbles--live" reviewers={presenceByCallId.current[params.data?.call_id] || []} />
            )
        },
        {
            headerName: "Actions",
            width: 100,
            pinned: "right",
            cellRenderer: (params) => (
                <div style={{ display: "flex", gap: "5px", justifyContent: "center" }} >
                    {params.data?.status == "done" && (
                        <i className="fas fa-file-alt action-button blue" onClick={() => onClickView({ row: params.data })} />
                    )}
                    {(user?.is_admin || hasPermission('refresh_conversation')) &&
                        <i className="fas fa-sync-alt action-button blue" onClick={() => onClickRefresh({ row: params.data })} />

                    }
                    {filters.processed === false && (user?.is_admin || hasPermission('delete_conversation_in_progress')) && (
                        <i className="fas fa-trash action-button red" onClick={() => onClickDelete({ row: params.data })} />
                    )}
                </div>
            )
        }
    ].filter(Boolean), [labelByUnitId, filters.processed]);

    useEffect(() => {
        const fetchCriterias = async () => {
            const [response, error] = await getCriteriasAll();

            if (error || !response) {
                console.error("Failed to fetch criterias:", error);
                return;
            }

            setCriteriasByCategory(response);
        };

        fetchCriterias();
    }, []);

    const categoryColors = useMemo(() => {
        const categories = Object.keys(criteriasByCategory);
        return categories.reduce((acc, category, index) => {
            acc[category] = CRITERIA_CATEGORY_COLORS[index % CRITERIA_CATEGORY_COLORS.length];
            return acc;
        }, {});
    }, [criteriasByCategory]);

    const groupedCriteriaOptions = useMemo(
        () =>
            Object.entries(criteriasByCategory).map(([category, items]) => ({
                label: category,
                color: categoryColors[category],
                options: (items || []).map((criteria) => ({
                    value: buildCriteriaKey(category, criteria.id),
                    label: criteria.name,
                    category
                }))
            })),
        [criteriasByCategory, categoryColors]
    );

    const criteriaOptions = useMemo(
        () => groupedCriteriaOptions.flatMap((group) => group.options),
        [groupedCriteriaOptions]
    );

    const unitGroupColorByName = useMemo(() => {
        const names = [...new Set(unitLabels.map((u) => u.label.split(" ")[0]))];
        return Object.fromEntries(names.map((name, i) => [name, CRITERIA_CATEGORY_COLORS[i % CRITERIA_CATEGORY_COLORS.length]]));
    }, [unitLabels]);

    const groupedUnitOptions = useMemo(() => {
        const groups = {};
        unitLabels.forEach((u) => {
            const groupName = u.label.split(" ")[0];
            groups[groupName] = groups[groupName] || [];
            groups[groupName].push({ value: u.unit_id, label: u.label, group: groupName });
        });
        return Object.entries(groups).map(([groupName, options]) => ({
            label: groupName,
            color: unitGroupColorByName[groupName],
            options,
        }));
    }, [unitLabels, unitGroupColorByName]);

    const unitOptionsFlat = useMemo(() => groupedUnitOptions.flatMap((g) => g.options), [groupedUnitOptions]);

    const criteriaResultOptions = [
        { value: "pass", label: "Passed" },
        { value: "fail", label: "Failed" },
        { value: "empty", label: "Not evaluated" },
    ];

    return (
        <PageTable>
            <Header title="List of Last conversations">

                {/*hasPermission("import_phones") ? (
                    <IconButton
                        onClick={showImportModal}
                        className="icons_up main-iconButton profil_action main_add-button space_tableButton"
                        icon="fas fa-upload"></IconButton>
                ) : null */}
            </Header >
            <div className="tabs_bar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex" }}>
                    <button
                        className={"tabs_item" + (filters.processed !== false ? " tabs_item-active" : "")}
                        onClick={() => setFilters(prev => ({ ...prev, processed: true }))}
                    >
                        Completed
                    </button>
                    <button
                        className={"tabs_item" + (filters.processed === false ? " tabs_item-active" : "")}
                        onClick={() => setFilters(prev => ({ ...prev, processed: false }))}
                    >
                        In progress
                    </button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {user?.is_admin && (
                        <button
                            type="button"
                            className="analyze-audio-button"
                            title="Transcribe and analyze an audio file"
                            onClick={() => ModalManager.show(AudioAnalysisModal)}
                        >
                            <i className="fas fa-wave-square"></i>
                            Analyze audio
                        </button>
                    )}
                    {user?.is_admin && bulkRefresh.isRunning && (
                        <button
                            type="button"
                            className="analyze-audio-button is-live"
                            title="A bulk refresh is running: click for the details"
                            onClick={() => ModalManager.show(BulkRefreshModal)}
                        >
                            <i className="fas fa-spinner fa-spin"></i>
                            Refreshing: {bulkRefresh.summary.remaining} left ({bulkRefresh.summary.done + bulkRefresh.summary.failed}/{bulkRefresh.summary.total})
                        </button>
                    )}
                    {user?.is_admin && filters.processed === false && (
                        <button
                            type="button"
                            className="analyze-audio-button"
                            title="Re-run the conversations still in progress over a period"
                            onClick={() => ModalManager.show(BulkRefreshModal)}
                        >
                            <i className="fas fa-rotate"></i>
                            Refresh in bulk
                        </button>
                    )}
                    {hasActiveFilters && (
                        <button className="reset-filters-button" onClick={handleResetFilters} title="Clear every filter back to defaults">
                            <i className="fas fa-times"></i>
                            Reset filters
                        </button>
                    )}
                </div>
            </div>
            {(filters.criteria_categories || []).length > 0 && (
                <div className="conversation-scope-pills">
                    {filters.criteria_categories.map((category) => (
                        <button key={category} type="button" className="conversation-scope-pill"
                            title="Remove this filter" onClick={() => handleRemoveScorecard(category)}
                        >
                            <span>Scorecard: {category}</span>
                            <i className="fas fa-times"></i>
                        </button>
                    ))}
                </div>
            )}
            <br></br>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }} key={filterResetKey}>
                <InputSearch name="call_id" placeholderLibelle="Search call" style={{ width: "210px", marginBottom: "10px" }}
                    defaultValue={filters.call_id}
                    onChange={(e) =>
                        setFilters(prev => ({
                            ...prev,
                            call_id: e.target.value
                        }))
                    }
                />
                <InputSearch name="client_id" placeholderLibelle="Search client" style={{ width: "210px", marginBottom: "10px" }}
                    defaultValue={filters.client_id}
                    onChange={(e) =>
                        setFilters(prev => ({
                            ...prev,
                            client_id: e.target.value
                        }))
                    }
                />
                {!isRestrictedToOwnConversations && (
                    <InputSearch name="agent_id" placeholderLibelle="Search agent" style={{ width: "210px", marginBottom: "10px" }}
                        defaultValue={filters.agent_id}
                        onChange={(e) =>
                            setFilters(prev => ({
                                ...prev,
                                agent_id: e.target.value
                            }))
                        }
                    />
                )}
                <Select
                    classNamePrefix="criteria-select"
                    isMulti
                    options={groupedUnitOptions}

                    value={unitOptionsFlat.filter(opt => filters.unit_ids.includes(opt.value))}

                    getOptionValue={(opt) => opt.value}

                    onChange={(selected) =>
                        setFilters(prev => ({
                            ...prev,
                            unit_ids: selected ? selected.map(s => s.value) : []
                        }))
                    }

                    hideSelectedOptions={false}

                    menuPortalTarget={document.body}

                    formatGroupLabel={(group) => (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                background: group.color
                            }} />
                            <span>{group.label}</span>
                        </div>
                    )}

                    formatOptionLabel={(opt) => (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{
                                width: "4px",
                                alignSelf: "stretch",
                                borderRadius: "2px",
                                background: unitGroupColorByName[opt.group]
                            }} />
                            <span>{opt.label}</span>
                        </div>
                    )}

                    styles={{
                        option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected ? "var(--secondary)" : base.backgroundColor,
                            color: state.isSelected ? "var(--main)" : base.color
                        }),
                        groupHeading: (base, state) => ({
                            ...base,
                            color: state.data.color,
                            fontWeight: 600,
                            fontSize: "0.85em",
                            textTransform: "none"
                        }),
                        container: (base) => ({
                            ...base,
                            width: "210px",
                            marginBottom: "10px"
                        }),
                        menu: (base) => ({
                            ...base,
                            marginTop: -10,
                        }),
                        menuList: (base) => ({
                            ...base,
                            overscrollBehavior: "contain",
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}

                    controlShouldRenderValue={false}
                    closeMenuOnSelect={false}

                    placeholder={
                        filters.unit_ids.length
                            ? `${filters.unit_ids.length} unit${filters.unit_ids.length > 1 ? "s" : ""} selected`
                            : "Filter by unit..."
                    }
                />
                <Tippy
                    className="date-input-tooltip"
                    content={
                        <div className="date-input-text-tooltip">
                            Scoring range
                        </div>
                    }
                    zIndex={9999}
                    placement="top"
                    delay={[200, 0]}
                >
                    <div>
                        <div className="container_scoring">
                            <div className="min_slider">Min: <b>{filters.score_min}</b></div>
                            <div className="max_slider">Max: <b>{filters.score_max}</b></div>
                        </div>
                        <div className="slider_container">

                            <Slider
                                className="score-slider"
                                range
                                min={0}
                                max={100}
                                value={[filters.score_min, filters.score_max]}
                                onChange={([min, max]) =>
                                    setFilters(prev => ({
                                        ...prev,
                                        score_min: min,
                                        score_max: max
                                    }))
                                }
                            />
                        </div>
                    </div>
                </Tippy>

                <div className="container_dates">
                    <Tippy
                        className="date-input-tooltip"
                        content={
                            <div className="date-input-text-tooltip">
                                Date Start
                            </div>
                        }
                        placement="top"
                        delay={[200, 0]}
                    >
                        <input
                            type="date"
                            className="input_search"
                            value={filters.date_start}
                            onChange={(e) =>
                                setFilters(prev => ({
                                    ...prev,
                                    date_start: e.target.value
                                }))
                            }
                        />
                    </Tippy>


                    <Tippy
                        className="date-input-tooltip"
                        content={
                            <div className="date-input-text-tooltip">
                                Date End
                            </div>
                        }
                        placement="top"
                        delay={[200, 0]}
                    >
                        <input type="date" className="input_search" value={filters.date_end}
                            onChange={(e) =>
                                setFilters(prev => ({
                                    ...prev,
                                    date_end: e.target.value
                                }))
                            }
                        />
                    </Tippy>
                </div>
                <Select
                    classNamePrefix="criteria-select"
                    isMulti
                    options={groupedCriteriaOptions}

                    value={criteriaOptions.filter(opt =>
                        filters.criteria_ids.includes(opt.value)
                    )}

                    getOptionValue={(opt) => opt.value}

                    onChange={(selected) =>
                        setFilters(prev => ({
                            ...prev,
                            criteria_ids: selected ? selected.map(s => s.value) : []
                        }))
                    }

                    hideSelectedOptions={false}

                    formatGroupLabel={(group) => (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                background: group.color
                            }} />
                            <span>{group.label}</span>
                        </div>
                    )}

                    formatOptionLabel={(opt) => (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{
                                width: "4px",
                                alignSelf: "stretch",
                                borderRadius: "2px",
                                background: categoryColors[opt.category]
                            }} />
                            <span>{opt.label}</span>
                        </div>
                    )}

                    styles={{
                        option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected ? "var(--secondary)" : base.backgroundColor,
                            color: state.isSelected ? "var(--main)" : base.color
                        }),
                        groupHeading: (base, state) => ({
                            ...base,
                            color: state.data.color,
                            fontWeight: 600,
                            fontSize: "0.85em",
                            textTransform: "none"
                        }),
                        container: (base) => ({
                            ...base,
                            width: "350px",
                            marginBottom: "10px",
                            zIndex: 2
                        }),
                        menu: (base) => ({
                            ...base,
                            marginTop: -10,
                        }),
                    }}

                    controlShouldRenderValue={false}
                    closeMenuOnSelect={false}

                    placeholder={
                        filters.criteria_ids.length
                            ? `${filters.criteria_ids.length} criteria selected`
                            : "Filter by criteria..."
                    }
                />
                <Select
                    classNamePrefix="criteria-select"
                    isMulti
                    options={criteriaResultOptions}

                    value={criteriaResultOptions.filter(opt =>
                        filters.criteria_results.includes(opt.value)
                    )}

                    getOptionValue={(opt) => opt.value}

                    onChange={(selected) =>
                        setFilters(prev => ({
                            ...prev,
                            criteria_results: selected ? selected.map(s => s.value) : []
                        }))
                    }

                    hideSelectedOptions={false}

                    styles={{
                        option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected ? "var(--secondary)" : base.backgroundColor,
                            color: state.isSelected ? "var(--main)" : base.color
                        }),
                        container: (base) => ({
                            ...base,
                            width: "350px",
                            marginBottom: "10px",
                            zIndex: 2
                        }),
                        menu: (base) => ({
                            ...base,
                            marginTop: -10,
                        }),
                    }}

                    controlShouldRenderValue={false}
                    closeMenuOnSelect={false}

                    placeholder={
                        filters.criteria_results.length
                            ? `${filters.criteria_results.length} type${filters.criteria_results.length > 1 ? "s" : ""} selected`
                            : "Filter by type..."
                    }
                />

            </div>
            <br></br>
            <div className="conversation-table-completed">
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "6px" }}>
                    <LiveUpdateIndicator lastUpdate={lastPresenceUpdate} />
                </div>
                <div className="ag-theme-alpine" style={{ width: "100%", height: "650px" }} >
                    <AgGridReact
                        enableCellTextSelection={true}
                        rowHeight={70}
                        rowModelType="infinite"
                        datasource={datasource}
                        cacheBlockSize={15}
                        pagination={true}
                        paginationPageSize={15}
                        columnDefs={columnDefs}
                        defaultColDef={{
                            sortable: true,
                            filter: true,
                            resizable: true
                        }}
                        loading={isLoading}
                        onGridReady={(params) => {
                            setGridApi(params.api);
                        }}
                        getRowStyle={(params) => {
                            if (!params.data) {
                                return { opacity: 0 };
                            }
                        }}
                    />
                </div>
            </div>
        </PageTable >
    );
}

export default ConversationTableCompleted;